import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Container, Input, Table } from 'reactstrap';
import classes from './LoadsContainer.module.scss';
import get from '../../../services/axios/get';
import del from '../../../services/axios/delete';
import Loader from '../../UI/Loader/Loader';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { customAlert } from '../../../store/actions/alert';
import Alert from '../../UI/Alert/Alert';
import patch from '../../../services/axios/patch';
import Modal from '../../../containers/Modal/Modal';
import { FaCaretDown, FaExclamationTriangle, FaPlus, FaSearch } from 'react-icons/fa';
import post from '../../../services/axios/post';
import Load, { Charge } from '../../../models/load';
import put from '../../../services/axios/put';
import { useNavigate } from 'react-router-dom';
import Driver from '../../../models/driver';
import Documents from '../Documents/Documents';
import Charges from '../Charges/Charges';
import Status from '../../../models/status';
import Account from '../../../models/account';
import PluginDepot from '../../../models/pluginDepot';
import { months } from '../../UI/DatePicker/Months/Months';
import LoadForm from '../LoadForm/LoadForm';
import LoadActionMenu from './LoadActionMenu/LoadActionMenu';
import { format } from 'date-fns';
import ReactDOM from 'react-dom';
import Invoice from '../Invoice/Invoice';
import { validateContainer, validateContainerChecksum } from '../../../helpers/validateContainer';
import ClientInvoice from '../ClientInvoice/ClientInvoice';
import Tracking from '../TrackingModal/Tracking';
import PreAdviseDetails from '../PreAdviseDetails/PreAdviseDetails';

interface LoadsContainerProps {
  loadStatus: 'unassigned' | 'assigned' | 'completed' | 'plugged';
  userType: string;
}

interface ColumnConfig {
  id: string;
  label: string;
  accessor: (load: Load) => React.ReactNode;
  sortable?: boolean;
  sortKey?: string;
  visible: {
    admin?: boolean;
    client?: boolean;
    driver?: boolean;
    owner?: boolean;
    packHouse?: boolean;
    plugDepot?: boolean;
    exporter?: boolean;
  };
  minWidth?: string;
  maxWidth?: string;
}

type PriceListEntry = {
  entityType: string;
  id: string;
  [date: string]: any;
};

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function extractGensetPrice(load: Load, subcontractorPriceList: PriceListEntry, loadPointRegions: Record<string, 'WC' | 'EC'>): string {
  if (!load.isGenset) return 'R 0';

  let price = '';
  const region = load.loadPoint1 == null ? 'EC' : loadPointRegions[load.loadPoint1];
  const subPriceList = extractPriceListDateForDate(subcontractorPriceList as any, load.loadDate, region);
  if (subPriceList && subPriceList.additionalCharges?.genset) {
    price = `R ${subPriceList.additionalCharges.genset}`;
  }
  return price || 'R 0';
}

function extractPriceListObjects(data: PriceListEntry[]): Record<string, any> {
  const result: Record<string, any> = {};

  data?.forEach((entry) => {
    const clientName = entry.clientName;
    const ECDates: Record<string, any> = {}
    const WCDates: Record<string, any> = {}

    if (entry.WC != null) {
      Object.keys(entry.WC).forEach((key) => {
        WCDates[key] = { ...entry.WC[key], ...WCDates?.[key] }
      })
    }

    if (entry.EC != null) {
      Object.keys(entry.EC).forEach((key) => {
        ECDates[key] = { ...entry.EC[key], ...ECDates?.[key] }
      })
    }

    result[clientName] = { WC: WCDates, EC: ECDates }
  });

  return result;
}

function extractPriceListDateForDate(data?: PriceListEntry, date?: string, region?: 'WC' | 'EC'): Record<string, any> | null {
  let relevantDate: Date | null = null

  if ((data?.EC == null && data?.WC == null) || date == null || region == null) {
    return null;
  }

  Object.keys(data?.[region] ?? {}).forEach((key) => {
    const priceListDate = new Date(key)
    const loadDateObject = new Date(date)

    priceListDate.setHours(0, 0, 0, 0)
    loadDateObject.setHours(0, 0, 0, 0)

    if (
      priceListDate <= loadDateObject && (
        relevantDate == null ||
        relevantDate < priceListDate
      )) {
      relevantDate = priceListDate
    }
  })

  if (relevantDate == null) {
    return null
  }

  const relevantDateString = relevantDate ? formatDate(relevantDate) : null

  if (relevantDateString == null) {
    return null
  }

  return data?.[region]?.[relevantDateString] ?? null
}

function formatDateWithWeekday(dateString: string | undefined): string {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return format(date, 'EEE, d MMM yyyy');
  } catch (e) {
    return dateString;
  }
}

const hourlyTimeOptions = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return { value: `${hour}:00`, label: `${hour}:00` };
});

const SearchableDropdown = ({
  options,
  value,
  onChange,
  className,
  placeholder = "Select..."
}: {
  options: { value: string, label: string, renderLabel?: React.ReactNode }[],
  value: string,
  onChange: (value: string) => void,
  className?: string,
  placeholder?: string
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Render the dropdown menu with portal to ensure it's not cut off
  const renderDropdownMenu = () => {
    if (!isOpen) return null;

    const menuStyle = {
      position: 'absolute' as const,
      top: `${dropdownPosition.top}px`,
      left: `${dropdownPosition.left}px`,
      width: `${Math.max(dropdownPosition.width, 150)}px`, // Ensure minimum width of 150px
      zIndex: 9999
    };

    return ReactDOM.createPortal(
      <div ref={dropdownRef} className={classes.dropdownMenu} style={menuStyle}>
        <div className={classes.searchInputContainer}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className={classes.dropdownSearchInput}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        </div>
        <div className={classes.optionsContainer}>
          {filteredOptions.length === 0 ? (
            <div className={classes.noOptions}>No options found</div>
          ) : (
            filteredOptions.map(option => (
              <div
                key={option.value}
                className={`${classes.option} ${option.value === value ? classes.selectedOption : ''}`}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                  setSearchTerm('');
                }}
              >
                {option.renderLabel || option.label}
              </div>
            ))
          )}
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className={`${classes.searchableDropdown} ${className || ''}`}>
      <div
        className={classes.dropdownToggle}
        onClick={() => setIsOpen(!isOpen)}
        ref={buttonRef}
      >
        {selectedOption ? (selectedOption.renderLabel || selectedOption.label) : placeholder}
      </div>
      {renderDropdownMenu()}
    </div>
  );
};

const LoadsContainer: React.FC<LoadsContainerProps> = ({ loadStatus, userType }) => {
  const dispatch = useAppDispatch();
  const isMounted = useRef(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletionId, setDeletionId] = useState<string | null>(null);
  const [completionId, setCompletionId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [pluggingId, setPluggingId] = useState<string | null>(null);
  const [loads, setLoads] = useState<Load[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateLoad, setShowCreateLoad] = useState(false);
  const [editingLoad, setEditingLoad] = useState<Load | null>(null);
  const [documentsLoad, setDocumentsLoad] = useState<Load | null>(null);
  const [invoicingLoad, setInvoicingLoad] = useState<Load | null>(null);
  const [additionalChargesLoad, setAdditionalChargesLoad] = useState<Load | null>(null);
  const navigate = useNavigate();
  const [invoicedFilterValue, setInvoicedFilterValue] = useState('all');
  const [driverFilterValue, setDriverFilterValue] = useState('all');
  const [monthFilterValue, setMonthFilterValue] = useState(new Date().getMonth());
  const [yearFilterValue, setYearFilterValue] = useState(new Date().getFullYear());
  const [sortBy, setSortBy] = useState<string>('client');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [clientInvoicingLoad, setClientInvoicingLoad] = useState<Load | null>(null);
  const [drivers, setDrivers] = useState<{ drivers: Driver[], isLoading: boolean }>({
    drivers: [], isLoading: true
  });
  const [statuses, setStatuses] = useState<{ statuses: Status[], isLoading: boolean }>({
    statuses: [], isLoading: true
  });
  const [priceLists, setPriceLists] = useState<Record<string, any>>({});
  const [subcontractorPriceList, setSubcontractorPriceList] = useState<Record<string, any>>({});
  const [isPriceListsLoading, setIsPriceListsLoading] = useState(true);
  const subPriceTotalRef = useRef(0);
  const clientPriceTotalRef = useRef(0);
  const gensetTotalRef = useRef(0);
  const extrasTotalRef = useRef(0);
  const subbieExtrasTotalRef = useRef(0);
  const user = useAppSelector(state => state.auth.user);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [accounts, setAccounts] = useState<{ accounts: Account[], isLoading: boolean }>({
    accounts: [], isLoading: true
  });
  const [pluginDepots, setPluginDepots] = useState<{ pluginDepots: PluginDepot[], isLoading: boolean }>({
    pluginDepots: [], isLoading: true
  });
  const [showETATimePicker, setShowETATimePicker] = useState(false);
  const [selectedETALoad, setSelectedETALoad] = useState<Load | null>(null);
  const [selectedETAStatus, setSelectedETAStatus] = useState<string | null>(null);
  const [selectedETAHour, setSelectedETAHour] = useState<string>('');
  const [selectedETAMinute, setSelectedETAMinute] = useState<string>('00');
  const [showPreAdvisedPicker, setShowPreAdvisedPicker] = useState(false);
  const [selectedPreAdvisedLoad, setSelectedPreAdvisedLoad] = useState<Load | null>(null);
  const [showBookingPicker, setShowBookingPicker] = useState(false);
  const [selectedBookingLoad, setSelectedBookingLoad] = useState<Load | null>(null);
  const [selectedBookingTime, setSelectedBookingTime] = useState<string>('');
  const [selectedBookingNumber, setSelectedBookingNumber] = useState<string>('');
  const [loadPointRegions, setLoadPointRegions] = useState<Record<string, 'WC' | 'EC'>>({});
  const [selectedTrackingLoad, setSelectedTrackingLoad] = useState<Load | null>(null);
  const [selectedPreAdviseDetailsLoad, setSelectedPreAdviseDetailsLoad] = useState<Load | null>(null);

  const createColumnConfig = (): ColumnConfig[] => {
    return [
      {
        id: 'client',
        label: userType === 'packHouse' || userType === 'plugDepot' || userType === 'exporter' ? 'F/F' : 'Client',
        accessor: (load) => {
          const clientName = load.client;
          if (userType === 'admin' && load.handler?.alias) {
            return (
              <div>
                <div>{clientName}</div>
                <div style={{ fontSize: '0.9em', color: '#666' }}>({load.handler.alias})</div>
              </div>
            );
          }
          return clientName;
        },
        sortable: true,
        sortKey: 'client',
        visible: { admin: true, client: false, driver: true, owner: false, packHouse: true, plugDepot: false, exporter: true }
      },
      {
        id: 'driver',
        label: 'Driver',
        accessor: (load) => {
          if (!load.driver) return '';

          const driverName = typeof load.driver === 'object' ? load.driver.alias : '';

          return driverName;
        },
        sortable: true,
        sortKey: 'driver',
        visible: { admin: true, client: false, driver: true, owner: true, packHouse: false, plugDepot: false, exporter: false }
      },
      {
        id: 'date',
        label: 'Date',
        accessor: (load) => formatDateWithWeekday(load.loadDate),
        sortable: true,
        sortKey: 'date',
        visible: {
          admin: loadStatus !== 'plugged',
          client: loadStatus !== 'plugged',
          driver: loadStatus !== 'plugged',
          owner: loadStatus !== 'plugged',
          packHouse: loadStatus !== 'plugged',
          plugDepot: loadStatus !== 'plugged',
          exporter: loadStatus !== 'plugged'
        }
      },
      {
        id: 'bookingRef',
        label: 'Booking Ref',
        accessor: (load) => load.bookingRef || '',
        visible: { admin: loadStatus !== 'plugged', client: true, driver: true, owner: false, packHouse: true, plugDepot: false, exporter: true }
      },
      {
        id: 'clientRef',
        label: 'Client Ref',
        accessor: (load) => {
          const total = (
            (parseFloat(load.vgmLoadpoint1 || '0') || 0) +
            (parseFloat(load.vgmLoadpoint2 || '0') || 0) +
            (parseFloat(load.containerTare || '0') || 0)
          )

          return (
            <>
              {load.clientRef || ''}
              {(load.vgmLoadpoint1 || load.vgmLoadpoint2 || load.containerTare) && (
                <div style={{ fontSize: '0.8em', color: total > 32500 ? '#ff0000' : '#6c757d' }}>
                  VGM: {total.toFixed(0)} kg
                </div>
              )}
            </>
          )
        },
        visible: { admin: true, client: true, driver: true, owner: false, packHouse: true, plugDepot: false, exporter: true }
      },
      {
        id: 'emptyDepot',
        label: 'Empty Depot',
        accessor: (load) => load.emptyDepot || '',
        visible: { admin: loadStatus === 'unassigned', client: loadStatus === 'unassigned', driver: false, owner: false, packHouse: false, plugDepot: false, exporter: loadStatus === 'unassigned' }
      },
      {
        id: 'pluginDepot',
        label: 'Plugin Depot',
        accessor: (load) => {
          if (typeof load.pluginDepot === 'string') {
            return load.pluginDepot;
          } else if (load.pluginDepot && typeof load.pluginDepot === 'object') {
            return (load.pluginDepot as PluginDepot).alias || '';
          }
          return '';
        },
        visible: { admin: loadStatus === 'plugged', client: loadStatus === 'plugged', driver: false, owner: loadStatus === 'plugged', packHouse: loadStatus === 'plugged', plugDepot: false, exporter: loadStatus === 'plugged' }
      },
      {
        id: 'datePlugged',
        label: 'Date Plugged',
        accessor: (load) => formatDateWithWeekday(load.datePlugged),
        visible: { admin: loadStatus === 'plugged', client: loadStatus === 'plugged', driver: false, owner: loadStatus === 'plugged', packHouse: loadStatus === 'plugged', plugDepot: loadStatus === 'plugged', exporter: loadStatus === 'plugged' }
      },
      {
        id: 'dateXHauled',
        label: 'Date XHauled',
        accessor: (load) => formatDateWithWeekday(load.dateXHauled),
        visible: { admin: loadStatus === 'plugged', client: loadStatus === 'plugged', driver: false, owner: loadStatus === 'plugged', packHouse: loadStatus === 'plugged', plugDepot: loadStatus === 'plugged', exporter: loadStatus === 'plugged' }
      },
      {
        id: 'vessel',
        label: 'Vessel',
        accessor: (load) => (
          <>
            {load.vessel || ''}
            {load.port && (
              <div style={{ fontSize: '0.8em', color: '#6c757d' }}>
                {load.port}
              </div>
            )}
          </>
        ),
        visible: {
          admin: loadStatus === 'unassigned' || loadStatus === 'assigned' || loadStatus === 'plugged',
          client: loadStatus !== 'completed',
          driver: loadStatus !== 'completed',
          owner: false,
          packHouse: false,
          plugDepot: true,
          exporter: loadStatus !== 'completed'
        }
      },
      {
        id: 'loadPoint1',
        label: 'Load Point 1',
        accessor: (load: Load) => (
          <>
            {load.loadPoint1}
            {load.vgmLoadpoint1 && (
              <div style={{ fontSize: '0.8em', color: '#6c757d' }}>
                NET: {load.vgmLoadpoint1}kg
              </div>
            )}
          </>
        ),
        visible: {
          admin: loadStatus !== 'plugged',
          client: loadStatus !== 'plugged',
          driver: loadStatus !== 'plugged',
          owner: true,
          packHouse: loadStatus !== 'plugged',
          plugDepot: loadStatus !== 'plugged',
          exporter: loadStatus !== 'plugged'
        }
      },
      {
        id: 'loadPoint2',
        accessor: (load: Load) => (
          <>
            {load.loadPoint2}
            {load.vgmLoadpoint2 && (
              <div style={{ fontSize: '0.8em', color: '#6c757d' }}>
                NET: {load.vgmLoadpoint2}kg
              </div>
            )}
          </>
        ),
        label: 'Load Point 2',
        visible: {
          admin: loadStatus !== 'plugged',
          client: loadStatus !== 'plugged',
          driver: loadStatus !== 'plugged',
          owner: true,
          packHouse: loadStatus !== 'plugged',
          plugDepot: false,
          exporter: loadStatus !== 'plugged'
        }
      },
      {
        id: 'genset',
        label: 'Genset',
        accessor: (load) => load.isGenset ? 'Yes' : 'No',
        visible: {
          admin: loadStatus !== 'plugged',
          client: loadStatus !== 'plugged',
          driver: loadStatus !== 'plugged',
          owner: false,
          packHouse: loadStatus !== 'plugged',
          plugDepot: loadStatus !== 'plugged',
          exporter: loadStatus !== 'plugged'
        }
      },
      {
        id: 'containerNo',
        label: 'Container No.',
        accessor: (load) => (
          <>
            {load.containerNumber || ''}
            {load.containerTare && (
              <div style={{ fontSize: '0.8em', color: '#6c757d' }}>
                TARE: {load.containerTare}kg
              </div>
            )}
          </>
        ),
        visible: {
          admin: loadStatus === 'completed' || loadStatus === 'assigned' || loadStatus === 'plugged',
          client: loadStatus === 'completed' || loadStatus === 'assigned' || loadStatus === 'plugged',
          driver: loadStatus === 'assigned',
          owner: true,
          packHouse: loadStatus === 'completed' || loadStatus === 'assigned' || loadStatus === 'plugged',
          plugDepot: loadStatus === 'completed' || loadStatus === 'assigned' || loadStatus === 'plugged',
          exporter: loadStatus === 'completed' || loadStatus === 'assigned' || loadStatus === 'plugged'
        }
      },
      {
        id: 'port',
        label: 'Port',
        accessor: (load) => load.port ? load.port : '',
        visible: {
          admin: false,
          client: false,
          driver: false,
          owner: false,
          packHouse: false,
          plugDepot: true,
          exporter: false
        }
      },
      {
        id: 'sealNumber',
        label: 'Seal Number',
        accessor: (load) => load.sealNumber || '',
        visible: {
          admin: loadStatus === 'assigned',
          client: loadStatus === 'assigned',
          driver: loadStatus === 'assigned',
          owner: false,
          packHouse: loadStatus === 'assigned',
          plugDepot: false,
          exporter: loadStatus === 'assigned'
        }
      },
      {
        id: 'temptaleNumber',
        label: 'Temptale Number',
        accessor: (load) => load.temptaleNumber || '',
        visible: {
          admin: loadStatus === 'assigned' || loadStatus === 'completed',
          client: loadStatus === 'assigned' || loadStatus === 'completed',
          driver: loadStatus === 'assigned' || loadStatus === 'completed',
          owner: loadStatus === 'assigned' || loadStatus === 'completed',
          packHouse: loadStatus === 'assigned' || loadStatus === 'completed',
          plugDepot: loadStatus === 'assigned' || loadStatus === 'completed',
          exporter: loadStatus === 'assigned' || loadStatus === 'completed'
        }
      },
      {
        id: 'clientPrice',
        label: 'Client Price',
        accessor: (load) => {
          let price = load.clientPrice;
          if (!price && load.loadPoint1) {
            const region = load.loadPoint1 == null ? 'EC' : loadPointRegions[load.loadPoint1];
            const priceList = extractPriceListDateForDate(priceLists?.[load.client], load.loadDate, region);
            if (priceList) {
              let clientPriceNumber = priceList[load.loadPoint1];
              if (priceList.netOverrides?.[load.loadPoint1] != null) {
                clientPriceNumber = priceList.netOverrides?.[load.loadPoint1];
                price = `R ${clientPriceNumber}`;
              } else if (clientPriceNumber != null) {
                let multiplier = 1;
                if (priceList.fuelLevyPercentage != null) {
                  multiplier += (priceList.fuelLevyPercentage / 100);
                }
                clientPriceNumber = (clientPriceNumber * multiplier).toFixed(0);
                price = `R ${clientPriceNumber}`;
              }
            }
          }
          return price || '';
        },
        visible: { admin: loadStatus === 'completed', client: false, driver: false, owner: false, packHouse: false, plugDepot: false, exporter: false }
      },
      {
        id: 'subPrice',
        label: userType === 'owner' ? 'Load Price' : 'Sub Price',
        accessor: (load) => {
          let price = load.subPrice;
          if (!price && load.loadPoint1) {
            const region = load.loadPoint1 == null ? 'EC' : loadPointRegions[load.loadPoint1];
            const subPriceList = extractPriceListDateForDate(subcontractorPriceList as any, load.loadDate, region);
            if (subPriceList && subPriceList[load.loadPoint1]) {
              let subPriceNumber = subPriceList[load.loadPoint1];
              if (subPriceList.netOverrides?.[load.loadPoint1] != null) {
                subPriceNumber = subPriceList.netOverrides[load.loadPoint1];
                price = `R ${subPriceNumber}`;
              } else if (subPriceNumber != null) {
                let multiplier = 1;
                if (subPriceList.fuelLevyPercentage != null) {
                  multiplier += (subPriceList.fuelLevyPercentage / 100);
                }
                subPriceNumber = (subPriceNumber * multiplier).toFixed(0);
                price = `R ${subPriceNumber}`;
              }
            }
          }

          if (price != null && !price.startsWith('R ')) {
            price = `R ${price}`;
          }

          return price || '';
        },
        visible: { admin: loadStatus === 'completed', client: false, driver: false, owner: loadStatus === 'completed', packHouse: false, plugDepot: false, exporter: false }
      },
      {
        id: 'gensetPrice',
        label: 'Genset',
        accessor: (load) => extractGensetPrice(load, subcontractorPriceList as any, loadPointRegions),
        visible: { admin: false, client: false, driver: false, owner: loadStatus === 'completed', packHouse: false, plugDepot: false, exporter: false }
      },
      {
        id: 'extras',
        label: userType === 'admin' ? 'Client Extras' : 'Extras',
        accessor: (load) => {
          let total = 0;
          try {
            // For owner users, show subbieCharges instead of client charges
            const charges = JSON.parse(userType === 'owner' ? (load.subbieCharges || '[]') : (load.charges || '[]')) as Charge[];
            total = charges.reduce((sum, charge) => {
              const amount = parseFloat(charge.amount?.toString().replace(/[^0-9.-]+/g, '') || '0');
              const quantity = charge.quantity || 1;
              return sum + (isNaN(amount) ? 0 : amount * quantity);
            }, 0);
          } catch (e) {
            // If parsing fails, return 0
          }
          return total > 0 ? `R ${total.toFixed(2)}` : 'R 0';
        },
        visible: { admin: loadStatus === 'completed', client: false, driver: false, owner: loadStatus === 'completed', packHouse: false, plugDepot: false, exporter: false }
      },
      {
        id: 'subbieExtras',
        label: 'Subbie Extras',
        accessor: (load) => {
          let total = 0;
          try {
            const charges = JSON.parse(load.subbieCharges || '[]') as Charge[];
            total = charges.reduce((sum, charge) => {
              const amount = parseFloat(charge.amount?.toString().replace(/[^0-9.-]+/g, '') || '0');
              const quantity = charge.quantity || 1;
              return sum + (isNaN(amount) ? 0 : amount * quantity);
            }, 0);
          } catch (e) {
            // If parsing fails, return 0
          }
          return total > 0 ? `R ${total.toFixed(2)}` : 'R 0';
        },
        visible: { admin: loadStatus === 'completed', client: false, driver: false, owner: false, packHouse: false, plugDepot: false, exporter: false }
      },
      {
        id: 'invoiceNo',
        label: 'Invoice No.',
        accessor: (load) => load.invoiceNumber || '',
        visible: { admin: loadStatus === 'completed', client: false, driver: false, owner: false, packHouse: false, plugDepot: false, exporter: false }
      },
      {
        id: 'status',
        label: 'Load Status',
        accessor: (load) => {
          if (typeof load.status === 'string') {
            return load.status
          } else if (load.status && typeof load.status === 'object') {
            return (load.status as Status).alias || '';
          }
          return '';
        },
        visible: { admin: loadStatus === 'assigned', client: loadStatus === 'assigned', driver: loadStatus === 'assigned', owner: loadStatus === 'assigned', packHouse: loadStatus === 'assigned', plugDepot: loadStatus === 'assigned', exporter: loadStatus === 'assigned' }
      },
      {
        id: 'bookingNumber',
        label: 'Booking Number',
        accessor: (load) => (
          <>
            {load.bookingNumber || ''}
            {load.bookingTime && (
              <div style={{ fontSize: '0.8em', color: '#6c757d' }}>
                Time: {load.bookingTime}
              </div>
            )}
          </>
        ),
        visible: { admin: loadStatus !== 'unassigned' && loadStatus !== 'completed', client: loadStatus === 'assigned', driver: loadStatus === 'assigned', owner: loadStatus === 'assigned', packHouse: false, plugDepot: loadStatus === 'assigned', exporter: loadStatus === 'assigned' }
      },
      {
        id: 'preAdvised',
        label: 'Pre Advised',
        accessor: (load) => {
          if (load.preAdvised) return 'Yes';
          if (load.dummyPreAdvised) return 'Dummy';
          return 'No';
        },
        visible: {
          admin: loadStatus === 'assigned' || loadStatus === 'plugged',
          client: loadStatus === 'assigned' || loadStatus === 'plugged',
          driver: loadStatus === 'assigned',
          owner: loadStatus === 'plugged',
          packHouse: false,
          plugDepot: loadStatus === 'assigned',
          exporter: false
        }
      },
      {
        id: 'account',
        label: 'Account',
        accessor: (load) => {
          if (typeof load.account === 'string') {
            return load.account;
          } else if (load.account && typeof load.account === 'object') {
            return (load.account as Account).alias || '';
          }
          return '';
        },
        visible: { admin: loadStatus === 'plugged', client: loadStatus === 'plugged', driver: false, owner: false, packHouse: false, plugDepot: loadStatus === 'plugged', exporter: false }
      },
      {
        id: 'tempSetting',
        label: 'Temp Setting',
        accessor: (load) => load.tempSetting || '',
        visible: { admin: false, client: false, driver: false, owner: false, packHouse: false, plugDepot: loadStatus === 'plugged', exporter: false }
      }
    ];
  };

  // Get visible columns for the current user
  const getVisibleColumns = (): ColumnConfig[] => {
    const columns = createColumnConfig();
    return columns.filter(column => {
      return column.visible[userType as keyof typeof column.visible] === true;
    });
  };

  const submitCharges = (charges: Charge[], loadId: string, chargeType: 'client' | 'subbie' = 'client'): void => {
    setAdditionalChargesLoad(null);
    setIsLoading(true);

    const patchData = chargeType === 'client'
      ? { charges: JSON.stringify(charges) }
      : { subbieCharges: JSON.stringify(charges) };

    patch(
      `/load/${loadId}`,
      patchData,
      () => {
        getLoads();
      },
      (err) => {
        if (isMounted.current) {
          dispatch(customAlert(true, err));
        }
      }
    )
  }

  const getPriceLists = useCallback(() => {
    setIsPriceListsLoading(true);

    get(
      user?.userType === 'admin' ? '/price-list' : user?.userType === 'owner' ? '/price-list/subcontractors' : `/price-list/${user?.clientId}`,
      {},
      (res) => {
        if (isMounted.current) {
          const parsedLists = extractPriceListObjects(
            user?.userType === 'admin' ? res.data : [res.data]
          )
          setPriceLists(parsedLists);

          if (user?.userType === 'admin') {
            get(
              '/price-list/subcontractors',
              {},
              (subRes) => {
                if (isMounted.current) {
                  const parsedSubList = extractPriceListObjects([subRes.data]);
                  setSubcontractorPriceList(parsedSubList['Sub Contractors'] ?? {});
                }
                setIsPriceListsLoading(false);
              },
              (subErr) => {
                if (isMounted.current) {
                  setIsPriceListsLoading(false);
                }
              }
            );
          } else if (user?.userType === 'owner') {
            const parsedSubList = extractPriceListObjects([res.data]);
            setSubcontractorPriceList(parsedSubList['Sub Contractors'] ?? {});
            setIsPriceListsLoading(false);
          } else {
            setIsPriceListsLoading(false);
          }
        } else {
          setIsPriceListsLoading(false);
        }
      },
      (err) => {
        if (isMounted.current) {
          dispatch(customAlert(true, 'Failed to fetch price lists.'));
        }
        setIsPriceListsLoading(false);
      }
    );
  }, [dispatch, user?.userType, user?.clientId]);

  const getDrivers = useCallback(() => {
    get(
      '/driver',
      {},
      (res) => {
        if (isMounted.current) {
          setDrivers({ drivers: res.data, isLoading: false });
        }
      },
      () => { }
    );
  }, []);

  const getStatuses = useCallback(() => {
    get(
      '/status',
      {},
      (res) => {
        if (isMounted.current) {
          setStatuses({ statuses: res.data, isLoading: false });
        }
      },
      () => { }
    );
  }, []);

  const getAccounts = useCallback(() => {
    get(
      '/account',
      {},
      (res) => {
        if (isMounted.current) {
          setAccounts({ accounts: res.data, isLoading: false });
        }
      },
      () => { }
    );
  }, []);

  const getPluginDepots = useCallback(() => {
    get(
      '/depot',
      {
        isPlugin: true
      },
      (res) => {
        if (isMounted.current) {
          setPluginDepots({ pluginDepots: res.data, isLoading: false });
        }
      },
      () => { }
    );
  }, []);

  const getLoadPointRegions = useCallback(() => {
    get(
      '/load-point',
      { isDischarge: false },
      (res) => {
        if (isMounted.current) {
          const regionMapping: Record<string, 'WC' | 'EC'> = {};
          res.data.forEach((loadPoint: { alias: string; region: 'WC' | 'EC' }) => {
            regionMapping[loadPoint.alias] = loadPoint.region;
          });
          setLoadPointRegions(regionMapping);
        }
      },
      () => { }
    );
  }, []);

  const getLoads = useCallback(() => {
    setIsLoading(true);
    setLoads([]);

    const queryParams: {
      loadStatus: string
      from?: string
      to?: string
    } = {
      loadStatus: loadStatus
    }

    if (loadStatus === 'completed') {
      const month = String(monthFilterValue + 1).padStart(2, '0')
      const fromDate = new Date(`${yearFilterValue}-${month}-01`)
      const toDate = new Date(fromDate);

      fromDate.setHours(0, 0, 0, 0)
      toDate.setHours(0, 0, 0, 0)
      toDate.setMonth(toDate.getMonth() + 1)
      queryParams.from = fromDate.toISOString()
      queryParams.to = toDate.toISOString()
    }

    get(
      `/load`,
      queryParams,
      (res) => {
        if (isMounted.current) {
          setLoads(res.data);
          setIsLoading(false);
        }
      },
      () => {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    );
  }, [loadStatus, yearFilterValue, monthFilterValue, userType]);

  const createLoad = useCallback((load: Load, onError?: () => void) => {
    // Create a copy of the load to avoid modifying the original directly
    const loadToSubmit = { ...load };

    // Ensure driver is properly formatted as an object with id and alias
    if (loadToSubmit.driver && typeof loadToSubmit.driver === 'string') {
      const driverId = loadToSubmit.driver;
      const driverObj = drivers.drivers.find(d => d.id === driverId);
      if (driverObj) {
        loadToSubmit.driver = {
          id: driverObj.id,
          alias: driverObj.alias
        } as Driver;
      } else {
        // If driver not found, set to null
        loadToSubmit.driver = null;
      }
    }

    post(
      '/load',
      loadToSubmit,
      (res) => {
        if (isMounted.current) {
          if (loadToSubmit.driver) {
            navigate('/loads/assigned');
          } else {
            navigate('/loads/unassigned');
          }
          getLoads();
          setShowCreateLoad(false);
        }
      },
      (err) => {
        if (isMounted.current) {
          if (onError != null) {
            onError();
          }
          dispatch(customAlert(true, err));
        }
      }
    );
  }, [dispatch, getLoads, navigate, drivers.drivers]);

  const putLoad = useCallback((load: Load, loadId: string, onError?: () => void) => {
    const loadToSubmit = { ...load };

    if (loadToSubmit.driver && typeof loadToSubmit.driver === 'string') {
      const driverId = loadToSubmit.driver;
      const driverObj = drivers.drivers.find(d => d.id === driverId);
      if (driverObj) {
        loadToSubmit.driver = {
          id: driverObj.id,
          alias: driverObj.alias
        } as Driver;
      } else {
        loadToSubmit.driver = null;
      }
    }

    put(
      `/load/${loadId}`,
      loadToSubmit,
      (res) => {
        if (isMounted.current) {
          if (loadToSubmit.driver && loadToSubmit.loadStatus !== 'completed') {
            navigate('/loads/assigned');
          }
          getLoads();
          setEditingLoad(null);
          setInvoicingLoad(null);
        }
      },
      (err) => {
        if (isMounted.current) {
          if (onError != null) {
            onError();
          }
          dispatch(customAlert(true, err));
        }
      }
    );
  }, [dispatch, getLoads, navigate, drivers.drivers]);

  const patchLoadField = useCallback((loadId: string, fieldName: string, value: any, onError?: () => void, onSuccess?: () => void) => {
    const patchData: Record<string, any> = {};

    if (fieldName === 'driver') {
      if (typeof value === 'string' && value && value !== '---') {
        const driverObj = drivers.drivers.find(d => d.id === value);
        if (driverObj) {
          patchData[fieldName] = {
            id: driverObj.id,
            alias: driverObj.alias
          };
        } else {
          patchData[fieldName] = null;
        }
      } else if (!value || value === '---') {
        patchData[fieldName] = null;
        patchData['loadStatus'] = 'unassigned';
      } else {
        patchData[fieldName] = value;
      }
    } else if (fieldName === 'account') {
      if (typeof value === 'string' && value) {
        const accountObj = accounts.accounts.find(a => a.id === value);
        if (accountObj) {
          patchData[fieldName] = accountObj.alias;
        } else {
          patchData[fieldName] = null;
        }
      } else if (!value) {
        patchData[fieldName] = null;
      } else {
        patchData[fieldName] = value;
      }
    } else if (fieldName === 'pluginDepot') {
      if (typeof value === 'string' && value) {
        const depotObj = pluginDepots.pluginDepots.find(d => d.id === value);
        if (depotObj) {
          patchData[fieldName] = depotObj.alias;
        } else {
          patchData[fieldName] = null;
        }
      } else if (!value) {
        patchData[fieldName] = null;
      } else {
        patchData[fieldName] = value;
      }
    } else if (fieldName === 'preAdvised') {
      // Handle the three states of preAdvised
      if (value === 'not-pre-advised') {
        patchData['preAdvised'] = false;
        patchData['dummyPreAdvised'] = false;
      } else if (value === 'dummy-pre-advised') {
        patchData['preAdvised'] = false;
        patchData['dummyPreAdvised'] = true;
      } else if (value === 'pre-advised') {
        patchData['preAdvised'] = true;
        patchData['dummyPreAdvised'] = false;
      } else {
        // Boolean value from checkbox - use as preAdvised and set dummyPreAdvised to false
        patchData['preAdvised'] = value;
        patchData['dummyPreAdvised'] = false;
      }
    } else {
      patchData[fieldName] = value;
    }

    if (fieldName === 'driver' && loadStatus === 'plugged') {
      patchData['loadStatus'] = 'assigned';
    }

    if (fieldName === 'containerNumber') {
      const { isValid, formattedValue } = validateContainer(value);
      if (!isValid) {
        dispatch(customAlert(true, 'Container number must have 7 numbers and 4 letters and no special characters'));
        return;
      }
      patchData[fieldName] = formattedValue;
    }

    patch(
      `/load/${loadId}`,
      patchData,
      () => {
        if (isMounted.current) {
          if (fieldName === 'driver' && loadStatus === 'plugged') {
            navigate('/loads/assigned');
          } else if (onSuccess != null) {
            onSuccess();
          } else {
            getLoads();
          }
        }
      },
      (err) => {
        if (isMounted.current) {
          if (onError != null) {
            onError();
          }
          dispatch(customAlert(true, err));
        }
      }
    );
  }, [dispatch, getLoads, loadStatus, navigate, drivers.drivers, accounts.accounts, pluginDepots.pluginDepots]);

  const duplicateLoad = useCallback((load: Load) => {
    const newLoad: Partial<Load> = {
      ...load,
      id: undefined,
      containerNumber: '',
      bookingNumber: '',
      sealNumber: '',
      invoiceNumber: '',
      charges: '[]',
      client: load.client,
      loadDate: load.loadDate,
      loadPoint1: load.loadPoint1,
      loadPoint2: load.loadPoint2,
      emptyDepot: load.emptyDepot,
      isGenset: load.isGenset,
      vessel: load.vessel || '',
      bookingRef: load.bookingRef || '',
      loadStatus: 'unassigned'
    };

    delete newLoad.driver;
    delete newLoad.id;
    delete newLoad.clientRef;

    post(
      '/load',
      newLoad as Load,
      (res) => {
        if (isMounted.current) {
          getLoads();
          dispatch(customAlert(false, 'Load duplicated successfully'));
        }
      },
      (err) => {
        if (isMounted.current) {
          dispatch(customAlert(true, err || 'Failed to duplicate load'));
        }
      }
    );
  }, [dispatch, getLoads]);

  const deleteLoad = useCallback((id: string) => {
    del(
      `/load/${id}`,
      {},
      () => {
        if (isMounted.current) {
          getLoads();
        }
      },
      (err) => {
        dispatch(customAlert(true, 'Oops! Something went wrong'));
      }
    );
  }, [getLoads, dispatch]);

  const setLoadStatus = useCallback((id: string, loadStatus: Load['loadStatus']) => {
    patch(
      `/load/${id}`,
      {
        loadStatus: loadStatus
      },
      () => {
        if (isMounted.current) {
          getLoads();
        }
      },
      (err) => {
        dispatch(customAlert(true, 'Oops! Something went wrong'));
      }
    );
  }, [getLoads, dispatch]);

  const findDuplicateClientRefs = (loads: Load[]): string[] => {
    const clientRefCounts = loads.reduce((acc, load) => {
      if (load.clientRef && load.clientRef.trim() !== '') {
        acc[load.clientRef] = (acc[load.clientRef] || 0) + 1;
      }
      return acc;
    }, {} as { [key: string]: number });

    return Object.keys(clientRefCounts).filter((clientRef) => clientRefCounts[clientRef] > 1);
  };

  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnId);
      setSortDirection('asc');
    }
  };

  const sortData = (data: Load[]): Load[] => {
    return [...data].sort((a, b) => {
      let valueA, valueB;

      switch (sortBy) {
        case 'client':
          valueA = a.client || '';
          valueB = b.client || '';
          break;
        case 'date':
          valueA = new Date(a.loadDate || '1000-01-01').getTime();
          valueB = new Date(b.loadDate || '1000-01-01').getTime();
          break;
        case 'driver':
          // Simply get the alias directly since it's guaranteed to exist
          valueA = a.driver && typeof a.driver === 'object' ? a.driver.alias : '';
          valueB = b.driver && typeof b.driver === 'object' ? b.driver.alias : '';
          break;
        default:
          valueA = a.client || '';
          valueB = b.client || '';
      }

      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filterLoads = (): Load[] => {
    let filteredLoads = loads.filter(load => {
      // Search filter
      const matchesSearch = Object.values(load).some(value => {
        if (typeof value === 'object' && value != null) {
          return Object.values(value).some(innerValue =>
            String(innerValue).toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
        return String(value).toLowerCase().includes(searchTerm.toLowerCase());
      });

      // Invoice filter
      let matchesInvoice = true;
      if (invoicedFilterValue === 'YES') {
        matchesInvoice = load.invoiceNumber != null && load.invoiceNumber !== '';
      } else if (invoicedFilterValue === 'NO') {
        matchesInvoice = load.invoiceNumber == null || load.invoiceNumber === '';
      }

      // Driver filter for owner
      let matchesDriver = true;
      if (driverFilterValue !== 'all' && load.driver) {
        const driverId = typeof load.driver === 'object' ? load.driver.id : load.driver;
        matchesDriver = driverId === driverFilterValue;
      }

      return matchesSearch && matchesInvoice && matchesDriver;
    });

    return sortData(filteredLoads);
  };

  // Update totals for completed loads
  const calculateTotals = (filteredLoads: Load[]) => {
    let subPriceTotal = 0;
    let clientPriceTotal = 0;
    let gensetTotal = 0;
    let extrasTotal = 0;
    let subbieExtrasTotal = 0;

    filteredLoads.forEach(load => {
      // Calculate sub price
      if (load.loadPoint1) {
        if (!load.subPrice) {
          const region = load.loadPoint1 == null ? 'EC' : loadPointRegions[load.loadPoint1];
          const subPriceList = extractPriceListDateForDate(subcontractorPriceList as any, load.loadDate, region);
          if (subPriceList && subPriceList[load.loadPoint1]) {
            let subPriceNumber = subPriceList[load.loadPoint1];
            if (subPriceList.netOverrides?.[load.loadPoint1] != null) {
              subPriceNumber = subPriceList.netOverrides[load.loadPoint1];
            } else if (subPriceNumber != null) {
              let multiplier = 1;
              if (subPriceList.fuelLevyPercentage != null) {
                multiplier += (subPriceList.fuelLevyPercentage / 100);
              }
              subPriceNumber = (subPriceNumber * multiplier).toFixed(0);
            }
            const numericValue = parseFloat(String(subPriceNumber));
            if (!isNaN(numericValue)) {
              subPriceTotal += numericValue;
            }
          }
        } else {
          const numericValue = parseFloat(load.subPrice.replace(/[^0-9.-]+/g, ''));
          if (!isNaN(numericValue)) {
            subPriceTotal += numericValue;
          }
        }

        if (!load.clientPrice) {
          const region = load.loadPoint1 == null ? 'EC' : loadPointRegions[load.loadPoint1];
          const priceList = extractPriceListDateForDate(priceLists?.[load.client], load.loadDate, region);
          if (priceList && priceList[load.loadPoint1]) {
            let clientPriceNumber = priceList[load.loadPoint1];
            if (priceList.netOverrides?.[load.loadPoint1] != null) {
              clientPriceNumber = priceList.netOverrides[load.loadPoint1];
            } else if (clientPriceNumber != null) {
              let multiplier = 1;
              if (priceList.fuelLevyPercentage != null) {
                multiplier += (priceList.fuelLevyPercentage / 100);
              }
              clientPriceNumber = Number((clientPriceNumber * multiplier).toFixed(0));
            }
            const numericValue = parseFloat(String(clientPriceNumber));
            if (!isNaN(numericValue)) {
              clientPriceTotal += numericValue;
            }
          }
        } else {
          const numericValue = parseFloat(load.clientPrice.replace(/[^0-9.-]+/g, ''));
          if (!isNaN(numericValue)) {
            clientPriceTotal += numericValue;
          }
        }
      }

      // Calculate genset price
      if (load.isGenset) {
        const region = load.loadPoint1 == null ? 'EC' : loadPointRegions[load.loadPoint1];
        const subPriceList = extractPriceListDateForDate(subcontractorPriceList as any, load.loadDate, region);
        if (subPriceList && subPriceList.additionalCharges?.genset) {
          const gensetPrice = parseFloat(String(subPriceList.additionalCharges.genset));
          if (!isNaN(gensetPrice)) {
            gensetTotal += gensetPrice;
          }
        }
      }

      // Calculate extras total for client charges
      try {
        const charges = JSON.parse(load.charges || '[]') as Charge[];
        const loadExtrasTotal = charges.reduce((sum, charge) => {
          const amount = parseFloat(charge.amount?.toString().replace(/[^0-9.-]+/g, '') || '0');
          const quantity = charge.quantity || 1;
          return sum + (isNaN(amount) ? 0 : amount * quantity);
        }, 0);
        extrasTotal += loadExtrasTotal;
      } catch (e) {
        // If parsing fails, continue
      }

      // Calculate extras total for subbie charges
      try {
        const subbieCharges = JSON.parse(load.subbieCharges || '[]') as Charge[];
        const loadSubbieExtrasTotal = subbieCharges.reduce((sum, charge) => {
          const amount = parseFloat(charge.amount?.toString().replace(/[^0-9.-]+/g, '') || '0');
          const quantity = charge.quantity || 1;
          return sum + (isNaN(amount) ? 0 : amount * quantity);
        }, 0);
        subbieExtrasTotal += loadSubbieExtrasTotal;
      } catch (e) {
        // If parsing fails, continue
      }
    });

    subPriceTotalRef.current = subPriceTotal;
    clientPriceTotalRef.current = clientPriceTotal;
    gensetTotalRef.current = gensetTotal;
    extrasTotalRef.current = extrasTotal;
    subbieExtrasTotalRef.current = subbieExtrasTotal;
  };

  const uploadBookingPhoto = (file: File, loadId: string, clientRef: string) => {
    const fileName = `Booking Photo.${clientRef}.jpeg`;

    get(
      '/document/upload',
      {
        documentName: fileName,
        loadId: loadId
      },
      async (response) => {
        const uploadResponse = await fetch(response.data.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });

        if (uploadResponse.ok) {
          post(
            '/document',
            {
              documentName: fileName,
              loadId: loadId
            },
            () => {
              if (fileInputRef.current != null) {
                fileInputRef.current.value = '';
              }

              getLoads();
            },
            (error: any) => {
              dispatch(customAlert(true, error));
            }
          );
        } else {
          dispatch(customAlert(true, 'Upload failed'));
        }
      },
      (error) => {
        dispatch(customAlert(true, error));
      }
    );
  };

  useEffect(() => {
    getLoads();

    if (user?.permissions?.viewPriceList === true) {
      getPriceLists();
    }

    if (loadStatus === 'completed' && (
      userType === 'owner' || userType === 'admin' || userType === 'client')
    ) {
      getLoadPointRegions();
    }

    if (userType === 'admin') {
      if (loadStatus === 'unassigned' || loadStatus === 'assigned') {
        getDrivers();
      }

      setSearchTerm('');
      setInvoicedFilterValue('all');
      getStatuses();
      getPriceLists();
      getAccounts();
      getPluginDepots();
    }

    if (userType === 'owner') {
      getPriceLists();
      getDrivers();
    }

    if (userType === 'plugDepot') {
      getStatuses();
    }
  }, [
    getLoads,
    getDrivers,
    loadStatus,
    setSearchTerm,
    getStatuses,
    getPriceLists,
    getAccounts,
    getPluginDepots,
    userType,
    user?.permissions?.viewPriceList
  ]);

  useEffect(() => {
    const now = new Date();
    setMonthFilterValue(now.getMonth());
    setYearFilterValue(now.getFullYear());
    setDriverFilterValue('all');

    if (tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  }, [loadStatus]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const filteredLoads = filterLoads();

  if (loadStatus === 'completed') {
    calculateTotals(filteredLoads);
  }

  const duplicateClientRefs = findDuplicateClientRefs(filteredLoads);

  const EditableField = ({
    value,
    onEdit,
    isEditable = true,
    options = null,
    type = 'text',
    isHalfTicked = false,
    validation
  }: {
    value: string | boolean,
    onEdit: (newValue: string | boolean) => void,
    isEditable?: boolean,
    options?: { value: string, label: string }[] | null,
    type?: 'text' | 'select' | 'checkbox' | 'date',
    isHalfTicked?: boolean
    validation?: (value: string) => string
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isEditing]);

    // Update editValue when value changes
    useEffect(() => {
      setEditValue(value);
    }, [value]);

    const handleBlur = () => {
      setIsEditing(false);
      if (editValue !== value) {
        onEdit(editValue);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        setIsEditing(false);
        onEdit(editValue);
      } else if (e.key === 'Escape') {
        setIsEditing(false);
        setEditValue(value);
      }
    };

    if (!isEditable || userType === 'owner') {
      return <span>{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}</span>;
    }

    if (type === 'checkbox') {
      return (
        <div className={classes.checkboxField}>
          <input
            type="checkbox"
            checked={typeof value === 'boolean' ? value : value === 'true'}
            onChange={(e) => {
              const newValue = e.target.checked;
              setEditValue(newValue);
              onEdit(newValue);
            }}
            className={`${classes.checkbox} ${isHalfTicked ? classes.halfTicked : ''}`}
          />
          <span className={classes.checkboxLabel}>
            {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value === 'true' ? 'Yes' : 'No'}
          </span>
        </div>
      );
    }

    // For date picker type
    if (type === 'date') {
      return (
        <div className={classes.datePickerField}>
          <input
            type="date"
            value={typeof editValue === 'string' ? editValue.split('T')[0] : ''}
            onChange={(e) => {
              setEditValue(e.target.value);
              onEdit(e.target.value);
            }}
            className={`form-control form-control-sm ${classes.datePicker}`}
          />
        </div>
      );
    }

    if (type === 'select' || options) {
      return (
        <SearchableDropdown
          options={options || []}
          value={typeof value === 'string' ? value : String(value)}
          onChange={(newValue) => {
            setEditValue(newValue);
            onEdit(newValue);
          }}
          className={`form-control-sm ${classes.selectField}`}
        />
      );
    }

    if (isEditing) {
      return (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
          value={typeof editValue === 'string' ? editValue : String(editValue)}
          onChange={(e) => {
            let newValue = e.target.value;
            if (validation) {
              newValue = validation(newValue);
            }
            setEditValue(newValue);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`form-control form-control-sm ${classes.activeEditableField}`}
        />
      );
    }

    return (
      <div
        className={classes.editableField}
        onClick={() => {
          setIsEditing(true);
          // Initialize edit value with the displayed value
          setEditValue(value);
        }}
      >
        {value || <span style={{ color: '#aaa', fontStyle: 'italic' }}>Click to edit</span>}
      </div>
    );
  };

  const handleETATimeSelect = () => {
    if (selectedETALoad && selectedETAStatus) {
      // Combine hour and minute into a single time string
      const formattedTime = selectedETAHour ? `${selectedETAHour}:${selectedETAMinute}` : '';

      patchLoadField(
        selectedETALoad.id,
        'status',
        selectedETAStatus,
        () => { },
        () => {
          patchLoadField(selectedETALoad.id, 'etaTime', formattedTime);
        }
      );
      setShowETATimePicker(false);
    }
  };

  const openETATimePicker = (load: Load, status: string) => {
    setSelectedETALoad(load);
    setSelectedETAStatus(status);

    if (load.etaTime) {
      const [hour, minute] = load.etaTime.split(':');
      setSelectedETAHour(hour || '');
      setSelectedETAMinute(minute || '00');
    } else {
      setSelectedETAHour('');
      setSelectedETAMinute('00');
    }

    setShowETATimePicker(true);
  };

  const handleBookingSelect = () => {
    if (selectedBookingLoad) {
      // Update both booking number and booking time
      const patchData = {
        bookingNumber: selectedBookingNumber,
        bookingTime: selectedBookingTime
      };

      patch(
        `/load/${selectedBookingLoad.id}`,
        patchData,
        () => {
          if (isMounted.current) {
            getLoads();
          }
        },
        (err) => {
          if (isMounted.current) {
            dispatch(customAlert(true, err));
          }
        }
      );
      setShowBookingPicker(false);
    }
  };

  const openBookingPicker = (load: Load) => {
    setSelectedBookingLoad(load);
    setSelectedBookingNumber(load.bookingNumber || '');
    setSelectedBookingTime(load.bookingTime || '');
    setShowBookingPicker(true);
  };

  const renderETATimePicker = () => {
    if (!showETATimePicker) return null;

    return (
      <Modal close={() => setShowETATimePicker(false)}>
        <div className={classes['eta-time-picker']}>
          <h6>Select ETA Time</h6>
          <div className="form-group mb-3">
            <div className="d-flex">
              <div className="flex-grow-1 mr-2" style={{ marginRight: '10px' }}>
                <label>Hour</label>
                <select
                  className="form-control"
                  value={selectedETAHour}
                  onChange={(e) => setSelectedETAHour(e.target.value)}
                >
                  <option value="">Select Hour</option>
                  {Array.from({ length: 24 }, (_, i) => (
                    <option
                      key={i}
                      value={i.toString().padStart(2, '0')}
                    >
                      {i.toString().padStart(2, '0')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-grow-1">
                <label>Minute</label>
                <select
                  className="form-control"
                  value={selectedETAMinute}
                  onChange={(e) => setSelectedETAMinute(e.target.value)}
                >
                  <option value="00">00</option>
                  <option value="30">30</option>
                </select>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-between">
            <Button
              style={{ backgroundColor: 'var(--theme-green)', color: 'white', border: 'none' }}
              onClick={handleETATimeSelect}
            >
              Confirm
            </Button>
            <div onClick={() => setShowETATimePicker(false)} className={classes.cancel}>
              Cancel
            </div>
          </div>
        </div>
      </Modal>
    );
  };

  const renderPreAdvisedPicker = () => {
    if (!showPreAdvisedPicker) return null;

    return (
      <Modal close={() => setShowPreAdvisedPicker(false)}>
        <div className={classes['preadvised-picker']}>
          <h6>Select Pre Advised Status</h6>
          <div className="form-group mb-3">
            <div className="d-flex flex-column">
              <button
                className={`btn mb-2 ${!selectedPreAdvisedLoad?.preAdvised && !selectedPreAdvisedLoad?.dummyPreAdvised ? 'btn-outline-secondary' : 'btn-outline-secondary'}`}
                style={{
                  backgroundColor: !selectedPreAdvisedLoad?.preAdvised && !selectedPreAdvisedLoad?.dummyPreAdvised ? 'var(--theme-green)' : '',
                  color: !selectedPreAdvisedLoad?.preAdvised && !selectedPreAdvisedLoad?.dummyPreAdvised ? 'white' : ''
                }}
                onClick={() => {
                  if (selectedPreAdvisedLoad) {
                    patchLoadField(selectedPreAdvisedLoad.id, 'preAdvised', 'not-pre-advised');
                    setShowPreAdvisedPicker(false);
                  }
                }}
              >
                Not Pre Advised
              </button>
              <button
                className={`btn mb-2 ${!selectedPreAdvisedLoad?.preAdvised && selectedPreAdvisedLoad?.dummyPreAdvised ? 'btn-outline-secondary' : 'btn-outline-secondary'}`}
                style={{
                  backgroundColor: !selectedPreAdvisedLoad?.preAdvised && selectedPreAdvisedLoad?.dummyPreAdvised ? 'var(--theme-green)' : '',
                  color: !selectedPreAdvisedLoad?.preAdvised && selectedPreAdvisedLoad?.dummyPreAdvised ? 'white' : ''
                }}
                onClick={() => {
                  if (selectedPreAdvisedLoad) {
                    patchLoadField(selectedPreAdvisedLoad.id, 'preAdvised', 'dummy-pre-advised');
                    setShowPreAdvisedPicker(false);
                  }
                }}
              >
                Dummy Pre Advised
              </button>
              <button
                className={`btn ${selectedPreAdvisedLoad?.preAdvised ? 'btn-outline-secondary' : 'btn-outline-secondary'}`}
                style={{
                  backgroundColor: selectedPreAdvisedLoad?.preAdvised ? 'var(--theme-green)' : '',
                  color: selectedPreAdvisedLoad?.preAdvised ? 'white' : ''
                }}
                onClick={() => {
                  if (selectedPreAdvisedLoad) {
                    patchLoadField(selectedPreAdvisedLoad.id, 'preAdvised', 'pre-advised');
                    setShowPreAdvisedPicker(false);
                  }
                }}
              >
                Pre Advised
              </button>
            </div>
          </div>
          <div className="d-flex justify-content-end">
            <div
              onClick={() => setShowPreAdvisedPicker(false)}
              className={classes.cancel}
              style={{ cursor: 'pointer' }}
            >
              Cancel
            </div>
          </div>
        </div>
      </Modal>
    );
  };

  const renderBookingPicker = () => {
    if (!showBookingPicker) return null;

    return (
      <Modal close={() => setShowBookingPicker(false)}>
        <div className={classes['booking-picker']}>
          <h6>Edit Booking Details</h6>
          <div className="form-group mb-3">
            <label>Booking Number</label>
            <input
              type="text"
              className="form-control"
              value={selectedBookingNumber}
              onChange={(e) => setSelectedBookingNumber(e.target.value)}
              placeholder="Enter booking number"
            />
          </div>
          <div className="form-group mb-3">
            <label>Booking Time</label>
            <select
              className="form-control"
              value={selectedBookingTime}
              onChange={(e) => setSelectedBookingTime(e.target.value)}
            >
              <option value="">Select Time</option>
              {hourlyTimeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="d-flex justify-content-between">
            <Button
              style={{ backgroundColor: 'var(--theme-green)', color: 'white', border: 'none' }}
              onClick={handleBookingSelect}
            >
              Confirm
            </Button>
            <div onClick={() => setShowBookingPicker(false)} className={classes.cancel}>
              Cancel
            </div>
          </div>
        </div>
      </Modal>
    );
  };

  // Show invoice dialog with download option
  const showInvoiceDialog = useCallback((load: Load) => {
    setInvoicingLoad(load);
  }, []);


  return (
    <>
      <input
        type="file"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={(event) => {
          const file = event?.target?.files?.[0];
          if (!file || !fileInputRef.current) {
            return;
          }

          const loadId = fileInputRef.current.dataset.loadId || '';
          const clientRef = fileInputRef.current.dataset.clientRef || '';
          uploadBookingPhoto(file, loadId, clientRef);
        }}
        accept=".jpg,.jpeg,.png"
      />

      {/* Modals */}
      {additionalChargesLoad && (
        <Modal close={() => setAdditionalChargesLoad(null)}>
          <Charges
            userType={userType}
            submitCharges={submitCharges}
            loadId={additionalChargesLoad.id}
            charges={additionalChargesLoad.charges ?? '[]'}
            subbieCharges={additionalChargesLoad.subbieCharges ?? '[]'}
            onClose={() => setAdditionalChargesLoad(null)}
          />
        </Modal>
      )}

      {selectedTrackingLoad && (
        <Modal close={() => setSelectedTrackingLoad(null)}>
          <Tracking
            load={selectedTrackingLoad}
            close={() => setSelectedTrackingLoad(null)}
          />
        </Modal>
      )}

      {clientInvoicingLoad && userType === 'admin' && (
        <Modal close={() => setClientInvoicingLoad(null)}>
          <ClientInvoice
            load={clientInvoicingLoad}
            close={() => setClientInvoicingLoad(null)}
            priceList={extractPriceListDateForDate(
              priceLists?.[clientInvoicingLoad.client],
              clientInvoicingLoad.loadDate,
              clientInvoicingLoad.loadPoint1 == null ? 'EC' : loadPointRegions[clientInvoicingLoad.loadPoint1]
            ) ?? {}}
          />
        </Modal>
      )}

      {documentsLoad && (
        <Modal close={() => setDocumentsLoad(null)}>
          <Documents
            load={documentsLoad}
            userType={userType}
            ownerViewRestricted={userType === 'owner'}
            onRefreshLoads={getLoads}
            onClose={() => setDocumentsLoad(null)}
          />
        </Modal>
      )}

      {selectedPreAdviseDetailsLoad && (
        <Modal close={() => setSelectedPreAdviseDetailsLoad(null)}>
          <PreAdviseDetails load={selectedPreAdviseDetailsLoad} close={() => setSelectedPreAdviseDetailsLoad(null)} />
        </Modal>
      )}

      {invoicingLoad && (
        <Modal close={() => {
          setInvoicingLoad(null);
        }}>
          <Invoice
            load={invoicingLoad}
            close={() => {
              setInvoicingLoad(null);
            }}
            onRefreshLoads={getLoads}
            gensetPrice={Number(extractGensetPrice(invoicingLoad, subcontractorPriceList as any, loadPointRegions).replace(/[^0-9.-]+/g, ''))}
          />
        </Modal>
      )}

      {showCreateLoad && (
        <Modal close={() => setShowCreateLoad(false)}>
          <LoadForm close={() => setShowCreateLoad(false)} createLoad={createLoad} load={null} />
        </Modal>
      )}

      {editingLoad && (
        <Modal close={() => setEditingLoad(null)}>
          <LoadForm close={() => setEditingLoad(null)} putLoad={putLoad} load={editingLoad} />
        </Modal>
      )}

      <Alert
        confirm={(confirmation) => {
          if (confirmation && deletionId) {
            deleteLoad(deletionId);
          }
          setDeletionId(null);
        }}
        message='Enter "1010" to confirm'
        isOpen={deletionId !== null}
        isDeleteConfirm
        ok={() => setDeletionId(null)}
      />

      <Alert
        confirm={(confirmation) => {
          if (confirmation && completionId) {
            setLoadStatus(completionId, 'completed');
          }
          setCompletionId(null);
        }}
        message="Are you sure you want to complete this load?"
        isOpen={completionId !== null}
        isConfirm
      />

      <Alert
        confirm={(confirmation) => {
          if (confirmation && pluggingId) {
            setLoadStatus(pluggingId, 'plugged');
          }
          setPluggingId(null);
        }}
        message="Are you sure you want to plug this load?"
        isOpen={pluggingId !== null}
        isConfirm
      />

      <Container fluid className={classes.loadContainer}>
        {/* Header and controls */}
        <div className={classes.controlsBar} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className={classes.searchContainer}>
            <div className={classes.searchBox}>
              <FaSearch className={classes.searchIcon} />
              <Input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={classes.searchInput}
              />
            </div>
          </div>

          {userType === 'owner' && (
            <div className={classes.filterItem} style={{ margin: '0 15px' }}>
              <label className={classes.filterLabel}>Driver</label>
              <select
                className={classes.filterSelect}
                value={driverFilterValue}
                onChange={(e) => setDriverFilterValue(e.target.value)}
              >
                <option value="all">All Drivers</option>
                {drivers.drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.alias}
                  </option>
                ))}
              </select>
            </div>
          )}

          {loadStatus === 'completed' && (
            <div className={classes.centerFilters} style={{ display: 'flex', justifyContent: 'center' }}>
              {(userType === 'admin' || userType === 'owner') && (
                <div className={classes.filterItem} style={{ margin: '0 15px' }}>
                  <label className={classes.filterLabel}>Invoiced</label>
                  <select
                    className={classes.filterSelect}
                    value={invoicedFilterValue}
                    onChange={(e) => setInvoicedFilterValue(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="YES">Yes</option>
                    <option value="NO">No</option>
                  </select>
                </div>
              )}

              <div className={classes.filterItem} style={{ margin: '0 15px' }}>
                <label className={classes.filterLabel}>Year</label>
                <select
                  className={classes.filterSelect}
                  value={yearFilterValue}
                  onChange={(e) => setYearFilterValue(Number(e.target.value))}
                >
                  {[0, 1, 2, 3, 4].map(value => {
                    const year = new Date().getFullYear() - value;
                    return (
                      <option key={`year-${year}`} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className={classes.filterItem} style={{ margin: '0 15px' }}>
                <label className={classes.filterLabel}>Month</label>
                <select
                  className={classes.filterSelect}
                  value={monthFilterValue}
                  onChange={(e) => setMonthFilterValue(Number(e.target.value))}
                >
                  {months.map((month, index) => (
                    <option key={`month-${index}`} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className={classes.rightControls}>
            {loadStatus === 'unassigned' && userType === 'admin' && (
              <Button
                style={{ backgroundColor: 'var(--theme-green)', color: 'white', border: 'none' }}
                className={classes.createButton}
                onClick={() => setShowCreateLoad(true)}
              >
                <FaPlus /> Create Load
              </Button>
            )}
          </div>
        </div>

        {/* Table of loads */}
        <div className={classes.tableContainer} ref={tableRef}>
          {isLoading || (isPriceListsLoading && userType === 'admin' && loadStatus === 'completed') ? (
            <div className={classes.loaderContainer}>
              <Loader size="large" />
            </div>
          ) : (
            <>
              <Table hover responsive className={classes.loadsTable} borderless striped>
                <thead className={classes.tableHeader}>
                  <tr>
                    {getVisibleColumns().map(column => (
                      <th
                        key={column.id}
                        className={column.sortable ? classes.sortableColumn : ''}
                        onClick={column.sortable ? () => handleSort(column.sortKey || column.id) : undefined}
                      >
                        {column.label}
                        {column.sortable && sortBy === (column.sortKey || column.id) && (
                          <FaCaretDown
                            className={`${classes.sortIcon} ${sortDirection === 'desc' ? classes.flipIcon : ''}`}
                          />
                        )}
                      </th>
                    ))}
                    {(userType === 'packHouse' || userType === 'plugDepot') && loadStatus === 'unassigned' ? null : userType === 'plugDepot' ? null : (
                      <th className={classes.actionsColumn}>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredLoads.length === 0 ? (
                    <tr>
                      <td colSpan={getVisibleColumns().length + 1} className={classes.noDataMessage}>
                        No loads found
                      </td>
                    </tr>
                  ) : (
                    filteredLoads.map((load) => {
                      const isDuplicate = load.clientRef ? duplicateClientRefs.includes(load.clientRef) : false;
                      const hasInvalidContainerChecksum = userType === 'admin'
                        && (load.containerNumber ?? '').trim() !== ''
                        && !validateContainerChecksum(load.containerNumber ?? '');

                      return (
                        <tr
                          key={load.id}
                          className={`${isDuplicate ? classes.duplicateRow : ''} ${hasInvalidContainerChecksum ? classes.invalidContainerRow : ''}`}
                        >
                          {/* TODO: This is trash, make it more readable */}
                          {getVisibleColumns().map(column => (
                            <td key={`${load.id}-${column.id}`}>
                              <>
                              {userType === 'owner' ? (
                                <>
                                  {column.accessor(load)}
                                  {column.id === 'status' && (load.status === 'ENR 2ND LP' || load.status === 'ENR 1ST LP' || load.status === 'ENR PORT') && load.etaTime && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                      <div style={{ fontSize: '0.8em', color: '#6c757d', fontStyle: 'italic' }}>ETA: {load.etaTime}</div>
                                    </div>
                                  )}
                                </>
                              ) : userType === 'admin' && column.id === 'containerNo' && (loadStatus === 'completed' || loadStatus === 'assigned') ? (
                                <>
                                  <EditableField
                                    value={load.containerNumber || ''}
                                    onEdit={(newValue) => {
                                      patchLoadField(load.id, 'containerNumber', newValue.toString());
                                    }}
                                  />
                                  {load.containerTare && (
                                    <div style={{ fontSize: '0.8em', color: '#6c757d' }}>
                                      TARE: {load.containerTare}kg
                                    </div>
                                  )}
                                </>
                              ) : userType === 'admin' && column.id === 'sealNumber' && loadStatus === 'assigned' ? (
                                <EditableField
                                  value={load.sealNumber || ''}
                                  validation={(value) => value.replace(/ /g, '').toUpperCase()}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'sealNumber', newValue.toString());
                                  }}
                                />
                              ) : (userType === 'admin' || userType === 'client' || userType === 'driver' || userType === 'owner' || userType === 'plugDepot' || userType === 'exporter') && column.id === 'bookingNumber' ? (
                                <div
                                  className={classes.editableField}
                                  onClick={() => openBookingPicker(load)}
                                  style={{ cursor: 'pointer', minHeight: '20px' }}
                                >
                                  {load.bookingNumber || load.bookingTime ? (
                                    column.accessor(load)
                                  ) : (
                                    <span style={{ color: '#aaa', fontStyle: 'italic' }}>Click to edit</span>
                                  )}
                                </div>
                              ) : (userType === 'admin' || userType === 'plugDepot') && column.id === 'status' ? (
                                <>
                                  <EditableField
                                    value={column.accessor(load) as string}
                                    onEdit={(newValue) => {
                                      const statusValue = newValue.toString();
                                      if (
                                        statusValue === 'ENR 1ST LP' ||
                                        statusValue === 'ENR 2ND LP' ||
                                        statusValue === 'ENR PORT'
                                      ) {
                                        openETATimePicker(load, statusValue);
                                      } else {
                                        patchLoadField(load.id, 'status', statusValue);
                                      }
                                    }}
                                    options={[
                                      { value: '', label: '---' },
                                      ...statuses.statuses.map(status => ({
                                        value: status.alias,
                                        label: status.alias
                                      })),
                                      { value: 'ENR 1ST LP', label: 'ENR 1ST LP' },
                                      { value: 'ENR 2ND LP', label: 'ENR 2ND LP' },
                                      { value: 'ENR PORT', label: 'ENR PORT' },
                                      { value: '*** COMPLETED ***', label: '*** COMPLETED ***' }
                                    ]}
                                    type="select"
                                  />

                                  {(load.status === 'ENR 2ND LP' || load.status === 'ENR 1ST LP' || load.status === 'ENR PORT') && load.etaTime && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <div style={{ fontSize: '0.8em', color: '#6c757d', fontStyle: 'italic' }}>ETA: {load.etaTime}</div>
                                    </div>
                                  )}
                                </>
                              ) : userType === 'admin' && column.id === 'driver' && loadStatus !== 'completed' ? (
                                <EditableField
                                  value={typeof load.driver === 'object' && load.driver ? load.driver.id : (load.driver || '')}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'driver', newValue.toString());
                                  }}
                                  options={[
                                    { value: '---', label: '---' },
                                    ...drivers.drivers.map(driver => ({
                                      value: driver.id,
                                      label: driver.alias,
                                      renderLabel: driver.alias
                                    }))
                                  ]}
                                  type="select"
                                />
                              ) : userType === 'admin' && column.id === 'datePlugged' ? (
                                <EditableField
                                  value={load.datePlugged || ''}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'datePlugged', newValue.toString());
                                  }}
                                  type="date"
                                />
                              ) : userType === 'admin' && column.id === 'dateXHauled' ? (
                                <EditableField
                                  value={load.dateXHauled || ''}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'dateXHauled', newValue.toString());
                                  }}
                                  type="date"
                                />
                              ) : userType === 'admin' && column.id === 'genset' ? (
                                <EditableField
                                  value={load.isGenset || false}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'isGenset', newValue);
                                  }}
                                  type="checkbox"
                                />
                              ) : userType === 'admin' && column.id === 'preAdvised' ? (
                                <div
                                  className={classes.checkboxField}
                                  onClick={() => {
                                    setSelectedPreAdvisedLoad(load);
                                    setShowPreAdvisedPicker(true);
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={load.preAdvised || false}
                                    readOnly
                                    className={`${classes.checkbox} ${!load.preAdvised && load.dummyPreAdvised ? classes.halfTicked : ''}`}
                                  />
                                  <span className={classes.checkboxLabel}>
                                    {load.preAdvised ? 'Yes' : (load.dummyPreAdvised ? 'Dummy' : 'No')}
                                  </span>
                                </div>
                              ) : userType === 'client' && column.id === 'preAdvised' && user?.permissions?.setPreAdvised ? (
                                <div
                                  className={classes.checkboxField}
                                  onClick={() => {
                                    setSelectedPreAdvisedLoad(load);
                                    setShowPreAdvisedPicker(true);
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={load.preAdvised || false}
                                    readOnly
                                    className={`${classes.checkbox} ${!load.preAdvised && load.dummyPreAdvised ? classes.halfTicked : ''}`}
                                  />
                                  <span className={classes.checkboxLabel}>
                                    {load.preAdvised ? 'Yes' : (load.dummyPreAdvised ? 'Dummy' : 'No')}
                                  </span>
                                </div>
                              ) : userType === 'admin' && column.id === 'clientPrice' && loadStatus === 'completed' ? (
                                <EditableField
                                  value={load.clientPrice || (() => {
                                    if (load.loadPoint1) {
                                      const region = load.loadPoint1 == null ? 'EC' : loadPointRegions[load.loadPoint1];
                                      const priceList = extractPriceListDateForDate(priceLists?.[load.client], load.loadDate, region);
                                      if (priceList) {
                                        let clientPriceNumber = priceList[load.loadPoint1];
                                        if (priceList.netOverrides?.[load.loadPoint1] != null) {
                                          clientPriceNumber = priceList.netOverrides?.[load.loadPoint1];
                                          return `R ${clientPriceNumber}`;
                                        } else if (clientPriceNumber != null) {
                                          let multiplier = 1;
                                          if (priceList.fuelLevyPercentage != null) {
                                            multiplier += (priceList.fuelLevyPercentage / 100);
                                          }
                                          clientPriceNumber = (clientPriceNumber * multiplier).toFixed(0);
                                          return `R ${clientPriceNumber}`;
                                        }
                                      }
                                    }
                                    return '';
                                  })()}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'clientPrice', newValue.toString());
                                  }}
                                />
                              ) : userType === 'admin' && column.id === 'subPrice' && loadStatus === 'completed' ? (
                                <EditableField
                                  value={load.subPrice || (() => {
                                    if (load.loadPoint1) {
                                      const region = load.loadPoint1 == null ? 'EC' : loadPointRegions[load.loadPoint1];
                                      const subPriceList = extractPriceListDateForDate(subcontractorPriceList as any, load.loadDate, region);
                                      if (subPriceList) {
                                        let subPriceNumber = subPriceList[load.loadPoint1];
                                        if (subPriceList.netOverrides?.[load.loadPoint1] != null) {
                                          subPriceNumber = subPriceList.netOverrides?.[load.loadPoint1];
                                          return `R ${subPriceNumber}`;
                                        } else if (subPriceNumber != null) {
                                          let multiplier = 1;
                                          if (subPriceList.fuelLevyPercentage != null) {
                                            multiplier += (subPriceList.fuelLevyPercentage / 100);
                                          }
                                          subPriceNumber = (subPriceNumber * multiplier).toFixed(0);
                                          return `R ${subPriceNumber}`;
                                        }
                                      }
                                    }
                                    return '';
                                  })()}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'subPrice', newValue.toString());
                                  }}
                                />
                              ) : userType === 'admin' && column.id === 'invoiceNo' && loadStatus === 'completed' ? (
                                <EditableField
                                  value={load.invoiceNumber || ''}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'invoiceNumber', newValue.toString());
                                  }}
                                />
                              ) : userType === 'admin' && column.id === 'account' && loadStatus === 'plugged' ? (
                                <EditableField
                                  value={(load.account && typeof load.account === 'object') ?
                                    (load.account as Account).id :
                                    accounts.accounts.find(a => a.alias === load.account)?.id || ''}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'account', newValue.toString());
                                  }}
                                  options={accounts.accounts.map(account => ({
                                    value: account.id,
                                    label: account.alias
                                  }))}
                                  type="select"
                                />
                              ) : userType === 'admin' && column.id === 'pluginDepot' && loadStatus === 'plugged' ? (
                                <EditableField
                                  value={(load.pluginDepot && typeof load.pluginDepot === 'object') ?
                                    (load.pluginDepot as PluginDepot).id :
                                    pluginDepots.pluginDepots.find(d => d.alias === load.pluginDepot)?.id || ''}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'pluginDepot', newValue.toString());
                                  }}
                                  options={pluginDepots.pluginDepots.map(depot => ({
                                    value: depot.id,
                                    label: depot.alias
                                  }))}
                                  type="select"
                                />
                              ) : column.id === 'temptaleNumber' && (loadStatus === 'assigned' || loadStatus === 'completed') ? (
                                userType === 'exporter' ? (
                                  <span>{load.temptaleNumber || ''}</span>
                                ) : (
                                  <EditableField
                                    value={load.temptaleNumber || ''}
                                    validation={(value) => value.toUpperCase().replace(/[^A-Z0-9]/g, '')}
                                    onEdit={(newValue) => {
                                      patchLoadField(load.id, 'temptaleNumber', newValue.toString());
                                    }}
                                  />
                                )
                              ) : column.id === 'preAdvised' && (userType === 'client' || userType === 'driver' || userType === 'owner' || userType === 'exporter') ? (
                                <span>
                                  {load.preAdvised ? 'Yes' : (load.dummyPreAdvised ? 'Dummy' : 'No')}
                                </span>
                              ) : (userType === 'client' || userType === 'driver' || userType === 'owner' || userType === 'exporter') && column.id === 'status' ? (
                                <>
                                  {column.accessor(load)}
                                  {(load.status === 'ENR 2ND LP' || load.status === 'ENR 1ST LP' || load.status === 'ENR PORT') && load.etaTime && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <div style={{ fontSize: '0.8em', color: '#6c757d', fontStyle: 'italic' }}>ETA: {load.etaTime}</div>
                                    </div>
                                  )}
                                </>
                              ) : userType === 'admin' && column.id === 'clientPrice' && loadStatus === 'completed' ? (
                                <EditableField
                                  value={load.clientPrice || (() => {
                                    if (load.loadPoint1) {
                                      const region = load.loadPoint1 == null ? 'EC' : loadPointRegions[load.loadPoint1];
                                      const priceList = extractPriceListDateForDate(priceLists?.[load.client], load.loadDate, region);
                                      if (priceList) {
                                        let clientPriceNumber = priceList[load.loadPoint1];
                                        if (priceList.netOverrides?.[load.loadPoint1] != null) {
                                          clientPriceNumber = priceList.netOverrides?.[load.loadPoint1];
                                          return `R ${clientPriceNumber}`;
                                        } else if (clientPriceNumber != null) {
                                          let multiplier = 1;
                                          if (priceList.fuelLevyPercentage != null) {
                                            multiplier += (priceList.fuelLevyPercentage / 100);
                                          }
                                          clientPriceNumber = (clientPriceNumber * multiplier).toFixed(0);
                                          return `R ${clientPriceNumber}`;
                                        }
                                      }
                                    }
                                    return '';
                                  })()}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'clientPrice', newValue.toString());
                                  }}
                                />
                              ) : userType === 'admin' && column.id === 'subPrice' && loadStatus === 'completed' ? (
                                <EditableField
                                  value={load.subPrice || (() => {
                                    if (load.loadPoint1) {
                                      const region = load.loadPoint1 == null ? 'EC' : loadPointRegions[load.loadPoint1];
                                      const subPriceList = extractPriceListDateForDate(subcontractorPriceList as any, load.loadDate, region);
                                      if (subPriceList) {
                                        let subPriceNumber = subPriceList[load.loadPoint1];
                                        if (subPriceList.netOverrides?.[load.loadPoint1] != null) {
                                          subPriceNumber = subPriceList.netOverrides?.[load.loadPoint1];
                                          return `R ${subPriceNumber}`;
                                        } else if (subPriceNumber != null) {
                                          let multiplier = 1;
                                          if (subPriceList.fuelLevyPercentage != null) {
                                            multiplier += (subPriceList.fuelLevyPercentage / 100);
                                          }
                                          subPriceNumber = (subPriceNumber * multiplier).toFixed(0);
                                          return `R ${subPriceNumber}`;
                                        }
                                      }
                                    }
                                    return '';
                                  })()}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'subPrice', newValue.toString());
                                  }}
                                />
                              ) : userType === 'admin' && column.id === 'invoiceNo' && loadStatus === 'completed' ? (
                                <EditableField
                                  value={load.invoiceNumber || ''}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'invoiceNumber', newValue.toString());
                                  }}
                                />
                              ) : userType === 'admin' && column.id === 'account' && loadStatus === 'plugged' ? (
                                <EditableField
                                  value={(load.account && typeof load.account === 'object') ?
                                    (load.account as Account).id :
                                    accounts.accounts.find(a => a.alias === load.account)?.id || ''}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'account', newValue.toString());
                                  }}
                                  options={accounts.accounts.map(account => ({
                                    value: account.id,
                                    label: account.alias
                                  }))}
                                  type="select"
                                />
                              ) : userType === 'admin' && column.id === 'pluginDepot' && loadStatus === 'plugged' ? (
                                <EditableField
                                  value={(load.pluginDepot && typeof load.pluginDepot === 'object') ?
                                    (load.pluginDepot as PluginDepot).id :
                                    pluginDepots.pluginDepots.find(d => d.alias === load.pluginDepot)?.id || ''}
                                  onEdit={(newValue) => {
                                    patchLoadField(load.id, 'pluginDepot', newValue.toString());
                                  }}
                                  options={pluginDepots.pluginDepots.map(depot => ({
                                    value: depot.id,
                                    label: depot.alias
                                  }))}
                                  type="select"
                                />
                              ) : (userType === 'packHouse' || userType === 'plugDepot') && column.id === 'status' ? (
                                <>
                                  {column.accessor(load)}
                                  {(load.status === 'ENR 2ND LP' || load.status === 'ENR 1ST LP' || load.status === 'ENR PORT') && load.etaTime && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <div style={{ fontSize: '0.8em', color: '#6c757d', fontStyle: 'italic' }}>ETA: {load.etaTime}</div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                column.accessor(load)
                              )}
                              {userType === 'admin' && column.id === 'containerNo' && hasInvalidContainerChecksum && (
                                <span className={classes.invalidChecksumBadge} title="Container number check digit failed (ISO 6346)">
                                  <FaExclamationTriangle className={classes.invalidChecksumBadgeIcon} aria-hidden />
                                  Invalid
                                </span>
                              )}
                              </>
                            </td>
                          ))}
                          <td className={classes.actionsCell}>
                            {(userType === 'packHouse' || userType === 'plugDepot') && loadStatus === 'unassigned' ? null : userType === 'plugDepot' ? null : (
                              <LoadActionMenu
                                load={load}
                                loadStatus={loadStatus}
                                userType={userType}
                                onDelete={() => setDeletionId(load.id)}
                                onDuplicate={() => duplicateLoad(load)}
                                onComplete={() => setCompletionId(load.id)}
                                onPlugLoad={() => setPluggingId(load.id)}
                                onEdit={() => setEditingLoad(load)}
                                onShowCharges={() => setAdditionalChargesLoad(load)}
                                onShowDocuments={() => setDocumentsLoad(load)}
                                onShowInvoicing={() => showInvoiceDialog(load)}
                                onUnComplete={(newStatus: string) => setLoadStatus(load.id, newStatus as Load['loadStatus'])}
                                onGenerateInvoice={(userType === 'admin' || userType === 'owner') ? () => setInvoicingLoad(load) : undefined}
                                onClientInvoice={() => setClientInvoicingLoad(load)}
                                onTrackLoad={() => setSelectedTrackingLoad(load)}
                                onPreAdviseDetails={() => setSelectedPreAdviseDetailsLoad(load)}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>

              {/* Show totals for completed loads for admin users */}
              {loadStatus === 'completed' && (userType === 'admin' || userType === 'owner') && (
                <div className={classes.totalsContainer}>
                  {userType === 'admin' && (
                    <>
                      <div className={classes.total}>
                        <span className={classes.totalLabel}>Total Client Price:</span>
                        <span className={classes.totalValue}>R{clientPriceTotalRef.current.toFixed(2)}</span>
                      </div>
                      <div className={classes.total}>
                        <span className={classes.totalLabel}>Total Client Extras:</span>
                        <span className={classes.totalValue}>R{extrasTotalRef.current.toFixed(2)}</span>
                      </div>
                      <div className={classes.total}>
                        <span className={classes.totalLabel}>Total Sub Price:</span>
                        <span className={classes.totalValue}>R{subPriceTotalRef.current.toFixed(2)}</span>
                      </div>
                      <div className={classes.total}>
                        <span className={classes.totalLabel}>Total Subbie Extras:</span>
                        <span className={classes.totalValue}>R{subbieExtrasTotalRef.current.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  {userType === 'owner' && (
                    <>
                      <div className={classes.total}>
                        <span className={classes.totalLabel}>Total Sub Price:</span>
                        <span className={classes.totalValue}>R{subPriceTotalRef.current.toFixed(2)}</span>
                      </div>
                      <div className={classes.total}>
                        <span className={classes.totalLabel}>Total Genset:</span>
                        <span className={classes.totalValue}>R{gensetTotalRef.current.toFixed(2)}</span>
                      </div>
                      <div className={classes.total}>
                        <span className={classes.totalLabel}>Total Extras:</span>
                        <span className={classes.totalValue}>R{subbieExtrasTotalRef.current.toFixed(2)}</span>
                      </div>
                      <div className={classes.total} style={{ fontWeight: 'bold' }}>
                        <span className={classes.totalLabel}>Grand Total:</span>
                        <span className={classes.totalValue}>
                          R{(subPriceTotalRef.current + gensetTotalRef.current + subbieExtrasTotalRef.current).toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Container>
      {renderETATimePicker()}
      {renderPreAdvisedPicker()}
      {renderBookingPicker()}
    </>
  );
};

export default LoadsContainer;
